import React from "react";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useReveal } from "@/hooks/useReveal";
import { IMAGES } from "@/lib/images";

const BrandCard = ({ idx, name, role, desc, cta, href, image, reverse, testid }) => (
  <article
    data-testid={testid}
    className={`grid md:grid-cols-12 gap-8 md:gap-14 items-center ${
      reverse ? "md:[&>.brand-img]:order-2" : ""
    }`}
  >
    <div className="brand-img md:col-span-7 relative group overflow-hidden">
      <div className="relative aspect-[4/3] md:aspect-[5/4] overflow-hidden">
        <img
          src={image}
          alt={name}
          className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-[1400ms]"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/30 via-transparent to-black/50" />
        <div className="absolute top-6 left-6 text-[10px] tracking-[0.32em] text-white/80 border border-white/40 px-3 py-1">
          0{idx} / PROSCENIO
        </div>
      </div>
    </div>
    <div className="md:col-span-5">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-6 h-[1px] bg-[var(--ps-gold)]" />
        <span className="text-xs tracking-[0.3em] text-[var(--ps-gold)]">{role}</span>
      </div>
      <h3 className="font-serif-display text-5xl md:text-6xl leading-[0.98] text-white mb-6">
        {name.split(" ")[0]}{" "}
        <span className="italic text-[var(--ps-gold)]">
          {name.split(" ").slice(1).join(" ")}
        </span>
      </h3>
      <p className="text-base md:text-lg text-zinc-300 leading-relaxed mb-8">{desc}</p>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testid}-cta`}
        className="inline-flex items-center gap-3 text-sm tracking-[0.2em] uppercase text-white hover:text-[var(--ps-gold)] transition-colors border-b border-white/30 hover:border-[var(--ps-gold)] pb-2"
      >
        {cta}
        <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
      </a>
    </div>
  </article>
);

const Brands = () => {
  const { t } = useLanguage();
  const { ref, visible } = useReveal();

  return (
    <section
      id="brands"
      data-testid="brands-section"
      className="relative py-28 md:py-40 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col mb-20">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-6 h-[1px] bg-[var(--ps-gold)]" />
            <span className="text-xs tracking-[0.3em] text-[var(--ps-gold)]">
              {t.brands.kicker}
            </span>
          </div>
          <h2
            data-testid="brands-title"
            className="font-serif-display text-4xl md:text-6xl leading-[1.02] text-white max-w-4xl"
          >
            {t.brands.title.split(" ").slice(0, 1).join(" ")}{" "}
            <span className="italic text-[var(--ps-gold)]">
              {t.brands.title.split(" ").slice(1).join(" ")}
            </span>
          </h2>
        </div>

        <div
          ref={ref}
          className={`space-y-28 md:space-y-40 reveal ${visible ? "is-visible" : ""}`}
        >
          <BrandCard
            idx={1}
            testid="brand-wedding"
            name={t.brands.wedding.name}
            role={t.brands.wedding.role}
            desc={t.brands.wedding.desc}
            cta={t.brands.wedding.cta}
            href="https://www.prosceniowedding.com/"
            image={IMAGES.brandWedding}
          />
          <BrandCard
            idx={2}
            testid="brand-ensemble"
            name={t.brands.ensemble.name}
            role={t.brands.ensemble.role}
            desc={t.brands.ensemble.desc}
            cta={t.brands.ensemble.cta}
            href="https://www.proscenioensemble.com/"
            image={IMAGES.brandEnsemble}
            reverse
          />
        </div>
      </div>
    </section>
  );
};

export default Brands;
